import type { FC } from "react";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import type { BadgeType, ButtonType } from "@similarweb/ui-components";
import { IconButton, Badge, Button } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { LARGE_CARD_WIDTH, STANDARD_CARD_WIDTH } from "app/pages/sales-intelligence/pages/premium-features/configs";
import type { PremiumFeatures } from "app/pages/sales-intelligence/pages/premium-features/types/enums";
import premiumFeatureTrackingService from "app/pages/sales-intelligence/services/tracking/premium-feature/trackingService";

import {
    StyledCardActionsWrapper,
    StyledCardContainer,
    StyledCardDescription,
    StyledCardHeader,
    StyledCardTag,
    StyledCardTagsWrapper,
    StyledCardTitle,
    StyledCardWrapper,
    StyledIconWrapper,
} from "./styles";

interface PremiumFeatureCardProps {
    title: string;
    description: string;
    imageUrl?: string;
    tags: readonly string[];
    icon?: IconName;
    badge?: BadgeType;
    userCount?: string;
    buttonText?: string;
    onClick?: () => void;
    type: PremiumFeatures;
    buttonType?: ButtonType;
    iconName?: IconName;
    size?: "md" | "lg";
}

export const PremiumFeatureCard: FC<PremiumFeatureCardProps> = ({
    title,
    description,
    imageUrl,
    tags,
    icon,
    badge,
    buttonText = translate("si.common.contact_us"),
    onClick,
    type,
    buttonType,
    iconName,
    size = "md",
}) => {
    const handleOnClick = () => {
        premiumFeatureTrackingService.trackPremiumFeatureAction(type);
        onClick?.();
    };

    return (
        <StyledCardWrapper size={size}>
            {imageUrl ? (
                <Flex>
                    <img
                        src={imageUrl}
                        width={size === "lg" ? LARGE_CARD_WIDTH : STANDARD_CARD_WIDTH}
                    />
                </Flex>
            ) : null}
            <StyledCardContainer>
                {icon ? (
                    <StyledIconWrapper>
                        <SWReactIcons
                            iconName={icon}
                            size="52px"
                        />
                    </StyledIconWrapper>
                ) : null}
                <StyledCardHeader>
                    <StyledCardTitle>{title}</StyledCardTitle>
                    {badge ? <Badge type={badge} /> : null}
                </StyledCardHeader>
                <StyledCardDescription>{description}</StyledCardDescription>
                {tags && tags.length > 0 ? (
                    <StyledCardTagsWrapper>
                        {tags.slice(0, 3).map((item: string) => (
                            <StyledCardTag key={`tag_${item}`}>{item}</StyledCardTag>
                        ))}
                    </StyledCardTagsWrapper>
                ) : null}
                <StyledCardActionsWrapper>
                    {iconName ? (
                        <IconButton
                            iconName={iconName}
                            type={buttonType ?? "outlined"}
                            onClick={handleOnClick}
                        >
                            {buttonText}
                        </IconButton>
                    ) : (
                        <Button
                            type={buttonType ?? "outlined"}
                            onClick={handleOnClick}
                        >
                            {buttonText}
                        </Button>
                    )}
                </StyledCardActionsWrapper>
            </StyledCardContainer>
        </StyledCardWrapper>
    );
};
